import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";

interface EnrollmentRow {
  enrollmentId: number;
  enquiryId: number;
  studentCode?: string | null;
  statusName?: string | null;
  batchName?: string | null;
  studentName?: string | null;
}

interface BulkStatusChangeModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "changed" | "skipped" | "forbidden" | "not_found" | "error";
  previousStatusId?: number | null;
  newStatusId?: number;
  reason?: string;
  error?: string;
}

const headerCellStyle: React.CSSProperties = {
  background: "var(--surface-page)",
  color: "var(--text-body)",
  fontSize: "0.7rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-default)",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const bodyCellStyle: React.CSSProperties = {
  padding: "8px 10px",
  fontSize: "0.8rem",
  color: "var(--text-strong)",
  borderBottom: "1px solid var(--border-subtle)",
};

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    changed:    { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:    { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found:  { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    forbidden:  { bg: "#fff7ed", fg: "#9a3412", border: "#fed7aa" },
    error:      { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkStatusChangeModal: React.FC<BulkStatusChangeModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const [statuses, setStatuses] = useState<Array<{ id: number; name: string }>>([]);
  const [reasons, setReasons] = useState<
    Array<{ id: number; name: string; enrollmentStatusId: number | null }>
  >([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [targetStatusId, setTargetStatusId] = useState<number | "">("");
  const [reasonId, setReasonId] = useState<number | "">("");
  const [statusChangeDate, setStatusChangeDate] = useState<string>(
    () => new Date().toISOString().slice(0, 10),
  );
  const [remarks, setRemarks] = useState("");

  const [selectedEnrollmentIds, setSelectedEnrollmentIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  // Flatten enrollments under selected enquiries; join student names for display.
  const enrollments: EnrollmentRow[] = useMemo(() => {
    const nameByEnquiryId = new Map<number, string>();
    for (const s of studentList) nameByEnquiryId.set(s.enquiryId, s.name);

    const out: EnrollmentRow[] = [];
    for (const eqId of selectedEnquiryIds) {
      const list = enquiryEnrollmentsMap[eqId] || [];
      for (const enr of list) {
        out.push({
          enrollmentId: enr.enrollmentId,
          enquiryId: eqId,
          studentCode: enr.studentCode,
          statusName: enr.statusName,
          batchName: enr.batchName,
          studentName: nameByEnquiryId.get(eqId) || "",
        });
      }
    }
    return out;
  }, [selectedEnquiryIds, enquiryEnrollmentsMap, studentList]);

  // Load lookups once when the modal opens
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    Promise.all([
      EnrollmentService.getLookups(),
      EnrollmentService.getStatusChangeReasons(),
    ])
      .then(([lkResp, rsResp]) => {
        if (cancelled) return;
        setStatuses(lkResp.data?.data?.statuses || []);
        setReasons(rsResp.data?.data?.reasons || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  // Reset everything on close
  const handleClose = () => {
    setTargetStatusId("");
    setReasonId("");
    setStatusChangeDate(new Date().toISOString().slice(0, 10));
    setRemarks("");
    setSelectedEnrollmentIds(new Set());
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  // When the enrollment list or target status changes, pre-select all
  // enrollments that aren't already at the target status.
  useEffect(() => {
    if (results) return; // don't refresh selection after submit
    const targetName = statuses.find((s) => s.id === targetStatusId)?.name?.toLowerCase();
    const next = new Set<number>();
    for (const e of enrollments) {
      const isAlreadyAtTarget =
        targetName && e.statusName && e.statusName.toLowerCase() === targetName;
      if (!isAlreadyAtTarget) next.add(e.enrollmentId);
    }
    setSelectedEnrollmentIds(next);
  }, [enrollments, targetStatusId, statuses, results]);

  const filteredReasons = useMemo(() => {
    if (!targetStatusId) return reasons;
    // Reasons either match the chosen status, or are unscoped (null)
    return reasons.filter(
      (r) => r.enrollmentStatusId == null || r.enrollmentStatusId === targetStatusId,
    );
  }, [reasons, targetStatusId]);

  const toggleEnrollment = (id: number) => {
    setSelectedEnrollmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllEnrollments = () => {
    if (selectedEnrollmentIds.size === enrollments.length) {
      setSelectedEnrollmentIds(new Set());
    } else {
      setSelectedEnrollmentIds(new Set(enrollments.map((e) => e.enrollmentId)));
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!targetStatusId) {
      setError("Pick a status to change to.");
      return;
    }
    if (!statusChangeDate) {
      setError("Student Status Change Date is required.");
      return;
    }
    if (!remarks.trim()) {
      setError("Remarks are required.");
      return;
    }
    if (selectedEnrollmentIds.size === 0) {
      setError("Select at least one enrollment to apply the change to.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkUpdateStatus({
        enrollmentIds: Array.from(selectedEnrollmentIds),
        statusId: Number(targetStatusId),
        reasonId: reasonId ? Number(reasonId) : null,
        statusChangeDate: statusChangeDate || null,
        remarks: remarks.trim() || null,
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk status change failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  const targetStatusName =
    statuses.find((s) => s.id === targetStatusId)?.name || "—";

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? "Change Status — Results" : "Change Status"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {loadingLookups ? (
              <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                <i className="fa fa-spinner fa-spin" /> Loading options…
              </div>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Change Status To <span style={{ color: "var(--status-danger)" }}>*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={targetStatusId}
                    onChange={(e) =>
                      setTargetStatusId(e.target.value ? Number(e.target.value) : "")
                    }
                  >
                    <option value="">Select status…</option>
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Reason
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={reasonId}
                    onChange={(e) =>
                      setReasonId(e.target.value ? Number(e.target.value) : "")
                    }
                    disabled={!targetStatusId}
                  >
                    <option value="">
                      {targetStatusId ? "(optional)" : "Pick target status first"}
                    </option>
                    {filteredReasons.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" style={{ maxWidth: 200 }}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Student Status Change Date{" "}
                    <span style={{ color: "var(--status-danger)" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={statusChangeDate}
                    onChange={(e) => setStatusChangeDate(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Remarks <span style={{ color: "var(--status-danger)" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Included in the audit trail"
                  />
                </Form.Group>

                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--text-strong)",
                    marginBottom: 6,
                  }}
                >
                  Enrollments to change ({selectedEnrollmentIds.size} of{" "}
                  {enrollments.length})
                </div>

                {enrollments.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      background: "#fefce8",
                      color: "#854d0e",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    The selected students have no enrollments. Close this dialog and
                    pick rows that have at least one enrollment.
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: 260,
                      overflow: "auto",
                      border: "1px solid var(--border-default)",
                      borderRadius: 6,
                    }}
                  >
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ ...headerCellStyle, width: 36, textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={
                                selectedEnrollmentIds.size === enrollments.length &&
                                enrollments.length > 0
                              }
                              onChange={toggleAllEnrollments}
                              style={{ margin: 0, cursor: "pointer" }}
                            />
                          </th>
                          <th style={headerCellStyle}>Enrollment</th>
                          <th style={headerCellStyle}>Student</th>
                          <th style={headerCellStyle}>Current Status</th>
                          <th style={headerCellStyle}>Batch</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enrollments.map((e) => {
                          const checked = selectedEnrollmentIds.has(e.enrollmentId);
                          const alreadyAtTarget =
                            targetStatusName !== "—" &&
                            e.statusName &&
                            e.statusName.toLowerCase() === targetStatusName.toLowerCase();
                          return (
                            <tr key={e.enrollmentId}>
                              <td style={{ ...bodyCellStyle, textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleEnrollment(e.enrollmentId)}
                                  style={{ margin: 0, cursor: "pointer" }}
                                />
                              </td>
                              <td style={bodyCellStyle}>
                                <span style={{ fontWeight: 600 }}>#{e.enrollmentId}</span>
                                {e.studentCode && (
                                  <span style={{ marginLeft: 6, color: "var(--text-muted)" }}>
                                    {e.studentCode}
                                  </span>
                                )}
                              </td>
                              <td style={bodyCellStyle}>
                                <span style={{ color: "var(--text-strong)" }}>{e.studentName}</span>
                                <span style={{ marginLeft: 6, color: "var(--text-faint)", fontSize: 11 }}>
                                  Enq {e.enquiryId}
                                </span>
                              </td>
                              <td style={bodyCellStyle}>
                                <span style={{ color: "var(--text-body)" }}>
                                  {e.statusName || "—"}
                                </span>
                                {alreadyAtTarget && (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 10,
                                      color: "#854d0e",
                                      background: "#fefce8",
                                      border: "1px solid var(--status-warning-border)",
                                      borderRadius: 8,
                                      padding: "1px 6px",
                                    }}
                                  >
                                    already at target
                                  </span>
                                )}
                              </td>
                              <td style={bodyCellStyle}>
                                <span style={{ color: "var(--text-body)" }}>
                                  {e.batchName || "—"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.changed ?? 0}</strong> changed
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "changed" && (
                          <span style={{ color: "var(--text-body)" }}>
                            {r.previousStatusId ?? "—"} → {r.newStatusId}
                          </span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_at_target_status"
                              ? "Already at target status"
                              : (r.reason || "")}
                          </span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "forbidden" && (
                          <span style={{ color: "#9a3412" }}>Not permitted</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                loadingLookups ||
                !targetStatusId ||
                !statusChangeDate ||
                !remarks.trim() ||
                selectedEnrollmentIds.size === 0
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Submitting…
                </>
              ) : (
                <>Submit</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkStatusChangeModal;
